<template>
  <v-dialog :value="value" max-width="600" persistent @input="$emit('input', $event)">
    <v-card class="rounded-lg" v-if="falta">
      <v-card-title class="primary white--text py-3 px-4 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-file-document-edit-outline</v-icon>
          <span class="text-h6 font-weight-bold">Detalhes da Justificativa de Falta</span>
        </div>
        <v-chip
          small
          :color="corStatus(falta.status)"
          class="font-weight-bold white--text text-capitalize"
        >
          {{ falta.status }}
        </v-chip>
      </v-card-title>

      <v-card-text class="pt-4 pb-2 px-4">
        <v-card outlined class="mb-4 grey lighten-5">
          <v-card-text class="py-3">
            <div class="subtitle-1 font-weight-bold primary--text mb-1">
              {{ falta.colaborador?.name || "—" }}
            </div>
            <v-row dense class="caption">
              <v-col cols="6">
                {{ falta.data_fim ? "Período da Falta:" : "Data da Falta:" }}
                <strong class="primary--text d-block">
                  {{ formatarData(falta.data_inicio) }}
                  <span v-if="falta.data_fim"> até {{ formatarData(falta.data_fim) }}</span>
                </strong>
              </v-col>
              <v-col cols="6">
                Enviado em:
                <strong class="grey--text text--darken-3 d-block">
                  {{ formatarDataHora(falta.created_at) }}
                </strong>
              </v-col>
            </v-row>

            <v-divider class="my-2"></v-divider>

            <div class="caption font-weight-bold grey--text">Motivo Informado:</div>
            <div class="body-2 font-italic grey--text text--darken-3 mt-1">
              "{{ falta.motivo }}"
            </div>

            <div v-if="falta.observacao" class="mt-2">
              <div class="caption font-weight-bold grey--text">Observação do Colaborador:</div>
              <div class="body-2 grey--text text--darken-2 mt-1">
                {{ falta.observacao }}
              </div>
            </div>

            <div v-if="falta.documento" class="mt-3">
              <v-btn
                outlined
                color="primary"
                small
                class="font-weight-bold"
                :loading="abrindoDocumento"
                @click="verDocumento"
              >
                <v-icon left small>mdi-file-document-outline</v-icon>
                Ver Documento Anexo
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <template v-if="falta.status !== 'pendente'">
          <v-card outlined class="mb-3 border-left-blue">
            <v-card-text class="py-2">
              <div class="caption font-weight-bold grey--text">Resultado da Avaliação:</div>
              <v-row dense class="caption mt-1">
                <v-col cols="6"
                  >Avaliado por: <strong>{{ falta.avaliador?.name || "—" }}</strong></v-col
                >
                <v-col cols="6"
                  >Data da Análise:
                  <strong>{{ formatarDataHora(falta.avaliado_em) }}</strong></v-col
                >
              </v-row>
              <div v-if="falta.observacao_avaliador" class="mt-2">
                <div class="caption font-weight-bold grey--text">Parecer do Avaliador:</div>
                <div class="body-2 font-italic grey lighten-4 pa-2 rounded mt-1 border">
                  "{{ falta.observacao_avaliador }}"
                </div>
              </div>
            </v-card-text>
          </v-card>
        </template>

        <template v-if="podeAvaliar">
          <v-divider class="my-3"></v-divider>
          <div class="subtitle-2 font-weight-bold primary--text mb-2">Avaliar Justificativa</div>
          <v-textarea
            v-model="observacao"
            label="Observação / Parecer do Avaliador (opcional)"
            placeholder="Digite suas considerações ou motivo..."
            outlined
            dense
            rows="3"
            counter="500"
            :disabled="!!avaliando"
            :rules="[(v) => !v || v.length <= 500 || 'Máximo 500 caracteres']"
          ></v-textarea>
        </template>
        <template v-else-if="falta.status === 'pendente'">
          <v-alert type="info" text dense class="mb-0">
            Você não pode avaliar uma justificativa que você mesmo enviou.
          </v-alert>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="py-3 px-4 bg-grey lighten-4">
        <v-btn text color="grey" :disabled="!!avaliando" @click="fechar">Fechar</v-btn>
        <v-spacer></v-spacer>
        <template v-if="podeAvaliar">
          <v-btn
            color="error"
            class="font-weight-bold mr-2"
            :loading="avaliando === 'reprovado'"
            :disabled="!!avaliando || observacao.length > 500"
            @click="avaliar('reprovado')"
          >
            <v-icon left small>mdi-close-circle</v-icon>
            Reprovar
          </v-btn>
          <v-btn
            color="success"
            class="font-weight-bold"
            :loading="avaliando === 'aprovado'"
            :disabled="!!avaliando || observacao.length > 500"
            @click="avaliar('aprovado')"
          >
            <v-icon left small>mdi-check-circle</v-icon>
            Aprovar
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "FaltaJustificadaDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    falta: { type: Object, default: null },
  },
  data() {
    return {
      observacao: "",
      avaliando: null,
      abrindoDocumento: false,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    podeAvaliar() {
      if (!this.falta || this.falta.status !== "pendente") return false;
      return this.user?.id !== this.falta.user_id;
    },
  },
  watch: {
    value(aberto) {
      if (aberto) this.observacao = "";
    },
  },
  methods: {
    fechar() {
      this.$emit("input", false);
    },
    corStatus(status) {
      return (
        { pendente: "amber darken-2", aprovado: "success", reprovado: "error" }[status] || "grey"
      );
    },
    formatarData(data) {
      if (!data) return "—";
      const [ano, mes, dia] = String(data).split("T")[0].split("-");
      return `${dia}/${mes}/${ano}`;
    },
    formatarDataHora(data) {
      if (!data) return "—";
      const d = new Date(data);
      return d.toLocaleString("pt-BR");
    },
    async verDocumento() {
      try {
        this.abrindoDocumento = true;
        await this.$services.colaboradorPontosService.abrirDocumentoFalta(this.falta.id);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.abrindoDocumento = false;
      }
    },
    async avaliar(status) {
      try {
        this.avaliando = status;
        await this.$services.colaboradorPontosService.avaliarFaltaJustificada(this.falta.id, {
          status,
          observacao_avaliador: this.observacao || null,
        });
        this.$toast.success(
          status === "aprovado" ? "Justificativa aprovada!" : "Justificativa reprovada."
        );
        this.$emit("avaliado");
        this.fechar();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.avaliando = null;
      }
    },
  },
};
</script>
